import { useCallback, useEffect, useState } from 'react';
import type { Company, Tenant, TenantHealth } from '@ecinsa/shared';
import { ApiError, adminApi, getToken, setToken, type UsuarioTerminal } from '../lib/api';
import { CambiarContrasena, Copiable, Dialog, Empty, Field, Pill } from '../components/ui';
import { clockTime } from '../lib/format';

/** Un túnel dado de alta en el hub, tal como lo cuenta el hub. */
interface HubTunnel {
  name: string;
  /** Su IP en la VPN (`10.30.0.X`), o null si el hub no la dice. */
  ip: string | null;
}

/** Lo que se ve en el campo vacío: recuerda por dónde va la dirección buena. */
const PLANTILLA_SL = 'https://10.30.0.';

/**
 * La dirección del Service Layer de un cliente: SIEMPRE por la IP de su túnel.
 *
 * Nunca por la IP de su red local. El túnel termina en un Windows que reenvía
 * con portproxy, y ese reenvío responde en la IP del túnel, no en la de su
 * tarjeta local.
 */
function urlServiceLayer(ip: string): string {
  return `https://${ip}:50000/b1s/v1`;
}

/**
 * Panel de alta de clientes.
 *
 * Aquí se da de alta un cliente entero sin tocar código: su túnel del hub, la
 * dirección de su Service Layer y sus sociedades con el CIF de cada una. Al
 * guardar, el cliente queda creado con su esquema de datos y su subdominio.
 */
export function AdminConsole({ tenantDomain }: { tenantDomain: string }) {
  const [authenticated, setAuthenticated] = useState(Boolean(getToken()));
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [health, setHealth] = useState<Record<string, TenantHealth>>({});
  const [hubTunnels, setHubTunnels] = useState<HubTunnel[]>([]);
  const [live, setLive] = useState<string[]>([]);
  const [editing, setEditing] = useState<Tenant | 'new' | null>(null);
  const [companyOf, setCompanyOf] = useState<{ tenant: Tenant; company: Company | null } | null>(null);
  const [deleting, setDeleting] = useState<Tenant | null>(null);
  const [params, setParams] = useState<Tenant | null>(null);
  const [gente, setGente] = useState<{ tenant: Tenant; vista: 'usuarios' | 'operarios' } | null>(null);
  const [toast, setToast] = useState<{ ok: boolean; text: string } | null>(null);

  const notify = (ok: boolean, text: string) => {
    setToast({ ok, text });
    setTimeout(() => setToast(null), ok ? 4000 : 9000);
  };

  const load = useCallback(async () => {
    try {
      const data = await adminApi.tenants();
      setTenants(data.tenants);
      setHealth(Object.fromEntries(data.health.map((h) => [h.tenantId, h])));
      // El hub manda. Si una versión antigua de la API no devuelve hubTunnels,
      // se usan los nombres sueltos y el panel funciona igual, sin IP.
      setHubTunnels(data.hubTunnels ?? data.hubClients.map((name) => ({ name, ip: null })));
      setLive(data.live);
      setAuthenticated(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setAuthenticated(false);
      else notify(false, (err as Error).message);
    }
  }, []);

  useEffect(() => {
    if (authenticated) void load();
  }, [authenticated, load]);

  if (!authenticated) return <AdminLogin onDone={() => void load()} />;

  return (
    <div className="app app--escritorio">
      <header className="topbar">
        <div>
          <div className="topbar__title">Terminal de producción · administración</div>
          <div className="topbar__sub">Alta y mantenimiento de clientes · {tenantDomain}</div>
        </div>
        <div className="topbar__spacer" />
        <button className="btn" onClick={() => void load()}>⟳ Actualizar</button>
        <CuentasEcinsa onAviso={notify} />
        <button className="btn btn--primary" onClick={() => setEditing('new')}>+ Nuevo cliente</button>
        <button
          className="btn btn--ghost"
          onClick={() => {
            setToken(null);
            setAuthenticated(false);
          }}
        >
          Salir
        </button>
      </header>

      <div className="content stack">
        {tenants.length === 0 ? (
          <Empty
            icon="🏭"
            title="Todavía no hay ningún cliente"
            body="Da de alta el primero con «Nuevo cliente». Necesitarás su túnel del hub, la dirección de su Service Layer y, para cada sociedad, su company_db y su CIF."
          />
        ) : null}

        {tenants.map((tenant) => {
          const state = health[tenant.slug];
          return (
            <section key={tenant.id} className="card stack">
              <div className="row">
                <div>
                  <div className="h2">{tenant.name}</div>
                  {/* La dirección es un enlace de verdad: desde el panel se
                      salta al terminal del cliente sin teclear nada. */}
                  <a
                    className="link small"
                    href={`https://${tenant.slug}.${tenantDomain}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://{tenant.slug}.{tenantDomain} ↗
                  </a>
                </div>
                <div className="spacer" />
                <a
                  className="btn"
                  href={`https://${tenant.slug}.${tenantDomain}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Abrir terminal ↗
                </a>
                <a
                  className="btn"
                  href={`https://${tenant.slug}.${tenantDomain}/monitor`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Monitor ↗
                </a>
                {live.includes(tenant.slug) ? <Pill tone="ok">En marcha</Pill> : <Pill>En reposo</Pill>}
                {state?.ready ? <Pill tone="ok">Configurado</Pill> : <Pill tone="danger">Incompleto</Pill>}
                {!tenant.active ? <Pill tone="warn">Desactivado</Pill> : null}
                <button className="btn" onClick={() => setEditing(tenant)}>Editar</button>
                <button
                  className="btn"
                  onClick={() =>
                    void adminApi
                      .reload(tenant.slug)
                      .then(() => notify(true, `${tenant.name}: configuración recargada`))
                      .then(load)
                      .catch((e: Error) => notify(false, e.message))
                  }
                >
                  Recargar
                </button>
                <button className="btn" onClick={() => setGente({ tenant, vista: 'usuarios' })}>
                  Usuarios
                </button>
                <button className="btn" onClick={() => setGente({ tenant, vista: 'operarios' })}>
                  Operarios
                </button>
                <button className="btn" onClick={() => setParams(tenant)}>
                  Parámetros
                </button>
                <button className="btn btn--danger" onClick={() => setDeleting(tenant)}>
                  Dar de baja
                </button>
              </div>

              <div className="row row--wrap small muted">
                <Pill tone="accent">Túnel {tenant.hubClient}</Pill>
                <span className="mono">{tenant.slBaseUrl}</span>
                <span>· tiempo máximo de espera {Math.round(tenant.slTimeoutMs / 1000)} s</span>
                <span>· certificado {tenant.slVerifySsl ? 'verificado' : 'sin verificar'}</span>
              </div>

              {state?.problems.length ? (
                <div className="toast toast--error" style={{ boxShadow: 'none' }}>
                  {state.problems.map((p) => (
                    <div key={p}>⛔ {p}</div>
                  ))}
                </div>
              ) : null}
              {state?.data ? (
                <div className="row row--wrap small muted" style={{ gap: 10 }}>
                  <Pill tone={state.data.withPin > 0 ? 'ok' : 'danger'}>
                    {state.data.withPin} de {state.data.employees} empleados con PIN
                  </Pill>
                  <Pill tone={state.data.supervisors > 0 ? 'ok' : 'warn'}>
                    {state.data.supervisors} jefes de producción
                  </Pill>
                  <Pill tone={state.data.withResource > 0 ? 'ok' : 'danger'}>
                    {state.data.withResource} en recursos de mano de obra
                  </Pill>
                  <Pill tone={state.data.releasedOrders > 0 ? 'ok' : 'warn'}>
                    {state.data.releasedOrders} OFs liberadas
                  </Pill>
                  <Pill tone={state.data.ordersWithPhase > 0 ? 'ok' : 'danger'}>
                    {state.data.ordersWithPhase} con fase
                  </Pill>
                  <Pill>{state.data.phases} fases</Pill>
                  <Pill>{state.data.stopReasons} causas de parada</Pill>
                </div>
              ) : null}

              {state?.warnings.length ? (
                <div className="toast" style={{ boxShadow: 'none', borderColor: 'rgba(251,191,36,.5)' }}>
                  {state.warnings.map((w) => (
                    <div key={w}>⚠️ {w}</div>
                  ))}
                </div>
              ) : null}

              <div className="row">
                <span className="eyebrow">Sociedades</span>
                <div className="spacer" />
                <button className="btn" onClick={() => setCompanyOf({ tenant, company: null })}>
                  + Añadir sociedad
                </button>
              </div>

              {tenant.companies.length === 0 ? (
                <p className="muted small">Sin sociedades. El terminal de este cliente no podrá entrar en SAP.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Sociedad</th>
                      <th>company_db</th>
                      <th>CIF</th>
                      <th>Usuario SAP</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {tenant.companies.map((company) => (
                      <tr key={company.id}>
                        <td>
                          {company.name}
                          {company.isDefault ? <> <Pill tone="accent">Predeterminada</Pill></> : null}
                          {!company.active ? <> <Pill tone="warn">Inactiva</Pill></> : null}
                        </td>
                        <td className="mono">{company.companyDb}</td>
                        <td className="mono">
                          {company.cif ? (
                            company.cif
                          ) : (
                            <span style={{ color: 'var(--warn)' }}>sin CIF · sale sin matrícula</span>
                          )}
                        </td>
                        <td className="mono small">{company.slUsername}</td>
                        <td>
                          <div className="row">
                            <button className="btn" onClick={() => setCompanyOf({ tenant, company })}>
                              Editar
                            </button>
                            <button
                              className="btn"
                              onClick={() =>
                                void adminApi
                                  .testCompany(tenant.slug, company.id)
                                  .then((r) =>
                                    notify(
                                      r.ok,
                                      r.ok
                                        ? `${r.message} (${r.ms} ms${r.sentCifHeader ? ', con CIF ' + r.cif : ', SIN CIF'})`
                                        : r.message,
                                    ),
                                  )
                                  .catch((e: Error) => notify(false, e.message))
                              }
                            >
                              Probar conexión
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          );
        })}
      </div>

      {editing ? (
        <TenantForm
          tenant={editing === 'new' ? null : editing}
          hubTunnels={hubTunnels}
          tenantDomain={tenantDomain}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            notify(true, message);
            void load();
          }}
          onError={(message) => notify(false, message)}
        />
      ) : null}

      {companyOf ? (
        <CompanyForm
          tenant={companyOf.tenant}
          company={companyOf.company}
          onClose={() => setCompanyOf(null)}
          onSaved={(message) => {
            setCompanyOf(null);
            notify(true, message);
            void load();
          }}
          onError={(message) => notify(false, message)}
        />
      ) : null}

      {gente ? (
        <Gente
          tenant={gente.tenant}
          vistaInicial={gente.vista}
          onClose={() => setGente(null)}
          onAviso={(ok, texto) => notify(ok, texto)}
          onCambio={() => void load()}
        />
      ) : null}

      {params ? (
        <TenantParameters
          tenant={params}
          onClose={() => setParams(null)}
          onSaved={(message) => {
            setParams(null);
            notify(true, message);
            void load();
          }}
          onError={(message) => notify(false, message)}
        />
      ) : null}

      {deleting ? (
        <DeleteTenant
          tenant={deleting}
          onClose={() => setDeleting(null)}
          onDone={(message) => {
            setDeleting(null);
            notify(true, message);
            void load();
          }}
          onError={(message) => notify(false, message)}
        />
      ) : null}

      {toast ? (
        <div className="toast-host">
          <div className={`toast ${toast.ok ? 'toast--ok' : 'toast--error'}`}>{toast.text}</div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Las personas de un cliente, desde el panel de administración.
 *
 * Dos cosas distintas que conviene no mezclar:
 *  · Usuarios: entran con contraseña al panel de dirección de su planta.
 *  · Operarios: empleados de SAP que entran al terminal de taller con un PIN.
 * Un jefe de producción suele ser las dos cosas, y por eso están juntas aquí.
 */
/**
 * Cuentas de ECINSA.
 *
 * Una sola cuenta por persona, válida en el panel de administración Y en el
 * panel de dirección de todos los clientes, presentes y futuros. Entra como
 * director, que es el papel con más permisos, así que ve y hace lo mismo que
 * cualquier usuario del cliente.
 *
 * No se reparten copias por cliente a propósito: si alguien deja la empresa,
 * se da de baja aquí una vez y deja de entrar en todas partes. Con una copia
 * por cliente siempre queda una viva en el que nadie recuerda.
 */
function CuentasEcinsa({ onAviso }: { onAviso: (ok: boolean, texto: string) => void }) {
  const [cuentas, setCuentas] = useState<Awaited<ReturnType<typeof adminApi.cuentasEcinsa>>['users'] | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [nuevo, setNuevo] = useState(false);
  const [form, setForm] = useState({ username: '', fullName: '', password: '' });
  const [ocupado, setOcupado] = useState(false);
  const [credencial, setCredencial] = useState<{ username: string; password: string } | null>(null);

  const cargar = useCallback(
    () => void adminApi.cuentasEcinsa().then((r) => setCuentas(r.users)).catch((e: Error) => onAviso(false, e.message)),
    [onAviso],
  );
  useEffect(() => {
    if (abierto) cargar();
  }, [abierto, cargar]);

  if (!abierto) {
    return (
      <button className="btn" onClick={() => setAbierto(true)}>
        Cuentas de ECINSA
      </button>
    );
  }

  return (
    <Dialog
      title="Cuentas de ECINSA"
      subtitle="Entran en este panel y en el de dirección de todos los clientes"
      onClose={() => setAbierto(false)}
      wide
    >
      <div className="stack">
        <p className="muted small" style={{ margin: 0 }}>
          Con esta cuenta se entra en{' '}
          <span className="mono">https://&lt;cliente&gt;.terminal.ecinsapp.es/direccion</span> de cualquier cliente, con
          el papel de director. La contraseña es la misma en todos: cambiarla aquí la cambia en todas partes.
        </p>

        {cuentas === null ? (
          <Empty icon="⏳" title="Cargando…" />
        ) : (
          <table className="table table--zebra">
            <thead>
              <tr><th>Usuario</th><th>Nombre</th><th>Última entrada</th><th /></tr>
            </thead>
            <tbody>
              {cuentas.map((c) => (
                <tr key={c.id}>
                  <td className="mono">{c.username}</td>
                  <td>{c.fullName}</td>
                  <td className="small muted">{c.lastLoginAt ? new Date(c.lastLoginAt).toLocaleString('es-ES') : 'nunca'}</td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        className="btn"
                        onClick={() => {
                          const elegida = window.prompt(`Nueva contraseña de ${c.username} (vacío = se genera)`);
                          if (elegida === null) return;
                          void adminApi
                            .passwordCuentaEcinsa(c.id, elegida.trim() || undefined)
                            .then((r) => setCredencial({ username: c.username, password: r.password }))
                            .catch((e: Error) => onAviso(false, e.message));
                        }}
                      >
                        Contraseña
                      </button>
                      <button
                        className="btn btn--danger"
                        onClick={() => {
                          if (!window.confirm(`¿Dar de baja a ${c.username}? Dejará de entrar en todos los clientes.`)) return;
                          void adminApi
                            .bajaCuentaEcinsa(c.id)
                            .then(() => { onAviso(true, `${c.username} dado de baja`); cargar(); })
                            .catch((e: Error) => onAviso(false, e.message));
                        }}
                      >
                        Baja
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {cuentas.length === 0 ? (
                <tr><td colSpan={4} className="muted small">Todavía no hay ninguna.</td></tr>
              ) : null}
            </tbody>
          </table>
        )}

        {nuevo ? (
          <div className="card card--flat stack">
            <div className="eyebrow">Nueva cuenta</div>
            <div className="grid-2">
              <Field label="Usuario (su correo)">
                <input
                  className="input"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="nombre@ecinsa.com"
                />
              </Field>
              <Field label="Nombre y apellidos">
                <input
                  className="input"
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Contraseña (vacío = se genera una)">
              <input
                className="input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="mínimo 8 caracteres"
              />
            </Field>
            <div className="row">
              <div className="spacer" />
              <button className="btn" onClick={() => setNuevo(false)}>Cancelar</button>
              <button
                className="btn btn--primary"
                disabled={ocupado || form.username.trim().length < 2 || form.fullName.trim().length < 2}
                onClick={() => {
                  setOcupado(true);
                  void adminApi
                    .crearCuentaEcinsa(form.username.trim(), form.fullName.trim(), form.password.trim() || undefined)
                    .then((r) => {
                      setCredencial({ username: r.username, password: r.password });
                      setForm({ username: '', fullName: '', password: '' });
                      setNuevo(false);
                      cargar();
                    })
                    .catch((e: Error) => onAviso(false, e.message))
                    .finally(() => setOcupado(false));
                }}
              >
                {ocupado ? 'Creando…' : 'Crear cuenta'}
              </button>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="spacer" />
            <button className="btn btn--primary" onClick={() => setNuevo(true)}>+ Nueva cuenta</button>
          </div>
        )}
      </div>

      {credencial ? (
        <Dialog title={`Cuenta ${credencial.username}`} onClose={() => setCredencial(null)}>
          <div className="stack">
            <Copiable etiqueta="Usuario" valor={credencial.username} />
            <Copiable etiqueta="Contraseña" valor={credencial.password} />
            <p className="muted small" style={{ margin: 0 }}>
              Vale en este panel y en el de dirección de todos los clientes. Apúntala ahora: no se vuelve a poder ver.
            </p>
            <button className="btn btn--primary btn--block" onClick={() => setCredencial(null)}>Entendido</button>
          </div>
        </Dialog>
      ) : null}
    </Dialog>
  );
}

function Gente({
  tenant, vistaInicial, onClose, onAviso, onCambio,
}: {
  tenant: Tenant;
  vistaInicial: 'usuarios' | 'operarios';
  onClose: () => void;
  onAviso: (ok: boolean, texto: string) => void;
  onCambio: () => void;
}) {
  const [vista, setVista] = useState(vistaInicial);
  const [credencial, setCredencial] = useState<
    { titulo: string; usuario?: string; password?: string; pin?: string; sobrescrito?: string | null } | null
  >(null);

  return (
    <Dialog title={`${tenant.name} · personas`} onClose={onClose} wide>
      <div className="stack">
        <div className="chips">
          <button className={`chip${vista === 'usuarios' ? ' chip--selected' : ''}`} onClick={() => setVista('usuarios')}>
            Usuarios del panel
          </button>
          <button className={`chip${vista === 'operarios' ? ' chip--selected' : ''}`} onClick={() => setVista('operarios')}>
            Operarios del terminal
          </button>
        </div>

        {vista === 'usuarios' ? (
          <UsuariosDelCliente tenant={tenant} onAviso={onAviso} onCredencial={setCredencial} />
        ) : (
          <OperariosDelCliente tenant={tenant} onAviso={onAviso} onCredencial={setCredencial} onCambio={onCambio} />
        )}

      </div>

      {/*
        La credencial va en su propio diálogo, encima de todo.
        Antes se pintaba debajo de la tabla: con sesenta empleados quedaba fuera
        de la pantalla y parecía que el botón no hacía nada, así que se pulsaba
        otra vez y se generaba otro PIN distinto. Un secreto que solo se puede
        ver una vez no puede depender de dónde esté el scroll.
      */}
      {credencial ? (
        <Dialog title={credencial.titulo} onClose={() => setCredencial(null)}>
          <div className="stack">
            {credencial.usuario ? <Copiable etiqueta="Usuario" valor={credencial.usuario} /> : null}
            {credencial.password ? <Copiable etiqueta="Contraseña" valor={credencial.password} /> : null}
            {credencial.pin ? <Copiable etiqueta="PIN del terminal" valor={credencial.pin} grande /> : null}
            {credencial.sobrescrito ? (
              <p className="small" style={{ margin: 0, color: 'var(--warn)' }}>
                ⚠️ El campo tenía antes el valor <span className="mono">{credencial.sobrescrito}</span> y se ha
                sustituido. Si era un dato real de la ficha del empleado, vuelve a ponerlo en SAP.
              </p>
            ) : null}
            <p className="muted small" style={{ margin: 0 }}>
              Apúntalo ahora: no se vuelve a poder ver. Aquí solo queda su huella.
            </p>
            <button className="btn btn--primary btn--block" onClick={() => setCredencial(null)}>
              Entendido
            </button>
          </div>
        </Dialog>
      ) : null}
    </Dialog>
  );
}

function UsuariosDelCliente({
  tenant, onAviso, onCredencial,
}: {
  tenant: Tenant;
  onAviso: (ok: boolean, texto: string) => void;
  onCredencial: (c: { titulo: string; usuario?: string; password?: string }) => void;
}) {
  const [users, setUsers] = useState<UsuarioTerminal[] | null>(null);
  const [nuevo, setNuevo] = useState(false);
  const [cambiando, setCambiando] = useState<UsuarioTerminal | null>(null);
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'director' | 'jefe' | 'operario'>('director');

  const cargar = useCallback(
    () => void adminApi.tenantUsers(tenant.slug).then((r) => setUsers(r.users)).catch((e: Error) => onAviso(false, e.message)),
    [tenant.slug, onAviso],
  );
  useEffect(cargar, [cargar]);

  if (!users) return <Empty icon="⏳" title="Cargando…" />;

  return (
    <div className="stack">
      <div className="row">
        <span className="muted small">
          Entran con contraseña en https://{tenant.slug}.terminal.ecinsapp.es/direccion
        </span>
        <div className="spacer" />
        <button className="btn btn--primary" onClick={() => setNuevo((v) => !v)}>
          {nuevo ? 'Cancelar' : '+ Nuevo usuario'}
        </button>
      </div>

      {nuevo ? (
        <div className="card card--flat stack">
          <div className="grid-2">
            <Field label="Usuario"><input className="input mono" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="direccion" /></Field>
            <Field label="Nombre y apellidos"><input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} /></Field>
          </div>
          <div className="chips">
            {([['director', 'Responsable de planta'], ['jefe', 'Jefe de producción'], ['operario', 'Consulta']] as const).map(([k, l]) => (
              <button key={k} className={`chip${role === k ? ' chip--selected' : ''}`} onClick={() => setRole(k)}>{l}</button>
            ))}
          </div>
          <button
            className="btn btn--primary"
            disabled={username.length < 2 || fullName.length < 2}
            onClick={() =>
              void adminApi
                .createTenantUser(tenant.slug, { username, fullName, role })
                .then((r) => {
                  onCredencial({ titulo: 'Usuario creado', usuario: r.user.username, password: r.password });
                  setNuevo(false);
                  setUsername('');
                  setFullName('');
                  cargar();
                })
                .catch((e: Error) => onAviso(false, e.message))
            }
          >
            Crear usuario
          </button>
        </div>
      ) : null}

      {cambiando ? (
        <CambiarContrasena
          usuario={cambiando.username}
          onCancelar={() => setCambiando(null)}
          onGuardar={(password) =>
            void adminApi
              .resetTenantUserPassword(tenant.slug, cambiando.id, password)
              .then((r) => {
                onCredencial({
                  titulo: r.elegida
                    ? `Contraseña de ${cambiando.username} cambiada`
                    : `Nueva contraseña de ${cambiando.username}`,
                  usuario: cambiando.username,
                  password: r.password,
                });
                setCambiando(null);
              })
              .catch((e: Error) => onAviso(false, e.message))
          }
        />
      ) : null}

      {users.length === 0 ? (
        <p className="muted small">Este cliente no tiene ningún usuario. Crea al menos el responsable de planta.</p>
      ) : (
        <table className="table">
          <thead><tr><th>Usuario</th><th>Nombre</th><th>Rol</th><th /></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} style={u.active ? undefined : { opacity: 0.55 }}>
                <td className="mono">{u.username}</td>
                <td>{u.fullName}</td>
                <td>
                  <Pill tone={u.role === 'director' ? 'accent' : 'default'}>
                    {u.role === 'director' ? 'Responsable' : u.role === 'jefe' ? 'Jefe de producción' : 'Consulta'}
                  </Pill>
                  {!u.active ? <> <Pill tone="warn">De baja</Pill></> : null}
                </td>
                <td>
                  <button className="btn" onClick={() => setCambiando(u)}>
                    Cambiar contraseña
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function OperariosDelCliente({
  tenant, onAviso, onCredencial, onCambio,
}: {
  tenant: Tenant;
  onAviso: (ok: boolean, texto: string) => void;
  onCredencial: (c: { titulo: string; pin?: string; sobrescrito?: string | null }) => void;
  onCambio: () => void;
}) {
  const [datos, setDatos] = useState<Awaited<ReturnType<typeof adminApi.tenantOperarios>> | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [nuevo, setNuevo] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', jobTitle: '', pin: '', resourceCode: '', esJefe: false });
  const [ocupado, setOcupado] = useState(false);
  const [pineando, setPineando] = useState<number | null>(null);

  const cargar = useCallback(
    () => void adminApi.tenantOperarios(tenant.slug).then(setDatos).catch((e: Error) => onAviso(false, e.message)),
    [tenant.slug, onAviso],
  );
  useEffect(cargar, [cargar]);

  if (!datos) return <Empty icon="⏳" title="Cargando empleados de SAP…" />;

  const texto = busqueda.trim().toLowerCase();
  const lista = datos.operarios
    .filter((o) => !texto || o.fullName.toLowerCase().includes(texto) || String(o.employeeId).includes(texto))
    .slice(0, 40);

  return (
    <div className="stack">
      <div className="row row--wrap">
        <span className="muted small">
          Empleados de SAP. El PIN se guarda en <span className="mono">{datos.campoPin}</span> de su ficha.
        </span>
        <div className="spacer" />
        <input className="input" style={{ width: 220 }} placeholder="Buscar" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        <button className="btn btn--primary" onClick={() => setNuevo((v) => !v)}>
          {nuevo ? 'Cancelar' : '+ Nuevo empleado'}
        </button>
      </div>

      {nuevo ? (
        <div className="card card--flat stack">
          <div className="grid-2">
            <Field label="Nombre"><input className="input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></Field>
            <Field label="Apellidos"><input className="input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></Field>
          </div>
          <div className="grid-2">
            <Field label="Puesto (opcional)"><input className="input" value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} /></Field>
            <Field label="PIN (vacío = se genera)">
              <input
                className="input mono"
                inputMode="numeric"
                value={form.pin}
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 15) })}
              />
            </Field>
          </div>
          <Field label="Recurso de mano de obra (es lo que le deja imputar)">
            <select className="input" value={form.resourceCode} onChange={(e) => setForm({ ...form, resourceCode: e.target.value })}>
              <option value="">— ninguno de momento —</option>
              {datos.recursos.map((r) => <option key={r.code} value={r.code}>{r.name} ({r.code})</option>)}
            </select>
          </Field>
          <div className="chips">
            <button
              className={`chip${form.esJefe ? ' chip--selected' : ''}`}
              disabled={!datos.rolJefe}
              title={datos.rolJefe ? '' : 'Elige antes el rol de jefe en Parámetros'}
              onClick={() => setForm({ ...form, esJefe: !form.esJefe })}
            >
              {form.esJefe ? 'Será jefe de producción' : 'No es jefe de producción'}
            </button>
            {!datos.rolJefe ? <span className="muted small" style={{ alignSelf: 'center' }}>Elige antes el rol de jefe en Parámetros.</span> : null}
          </div>
          <button
            className="btn btn--primary"
            disabled={ocupado || form.firstName.trim().length < 2 || form.lastName.trim().length < 2}
            onClick={() => {
              setOcupado(true);
              void adminApi
                .crearTenantOperario(tenant.slug, {
                  firstName: form.firstName,
                  lastName: form.lastName,
                  jobTitle: form.jobTitle || null,
                  ...(form.pin ? { pin: form.pin } : {}),
                  resourceCode: form.resourceCode || null,
                  esJefe: form.esJefe,
                })
                .then((r) => {
                  onCredencial({ titulo: `${form.firstName} ${form.lastName} · creado en SAP con el nº ${r.employeeId}`, pin: r.pin });
                  if (r.avisos.length) onAviso(false, r.avisos.join(' · '));
                  setNuevo(false);
                  setForm({ firstName: '', lastName: '', jobTitle: '', pin: '', resourceCode: '', esJefe: false });
                  cargar();
                  onCambio();
                })
                .catch((e: Error) => onAviso(false, e.message))
                .finally(() => setOcupado(false));
            }}
          >
            {ocupado ? 'Creando en SAP…' : 'Crear en SAP'}
          </button>
        </div>
      ) : null}

      <table className="table table--zebra">
        <thead><tr><th>Empleado</th><th>Nº</th><th>PIN</th><th>Recursos</th><th /></tr></thead>
        <tbody>
          {lista.map((o) => (
            <tr key={o.employeeId}>
              <td>
                {o.fullName}
                {o.supervisor ? <> <Pill tone="accent">Jefe</Pill></> : null}
                <div className="small muted">{o.jobTitle ?? ''}</div>
              </td>
              <td className="mono">{o.employeeId}</td>
              <td>{o.hasPin ? <Pill tone="ok">Sí</Pill> : <Pill tone="warn">Sin PIN</Pill>}</td>
              <td className="small mono">{o.resourceCodes.join(', ') || <span className="muted">ninguno</span>}</td>
              <td>
                {/*
                  Mientras va la petición el botón se bloquea: cada pulsación
                  genera un PIN NUEVO y anula el anterior, así que pulsar dos
                  veces deja al operario con uno que nadie ha visto.
                */}
                <button
                  className="btn"
                  disabled={pineando === o.employeeId}
                  onClick={() => {
                    setPineando(o.employeeId);
                    void adminApi
                      .pinTenantOperario(tenant.slug, o.employeeId)
                      .then((r) => {
                        onCredencial({ titulo: `PIN de ${o.fullName}`, pin: r.pin, sobrescrito: r.sobrescrito });
                        cargar();
                        onCambio();
                      })
                      .catch((e: Error) => onAviso(false, e.message))
                      .finally(() => setPineando(null));
                  }}
                >
                  {pineando === o.employeeId ? 'Generando…' : o.hasPin ? 'Nuevo PIN' : 'Generar PIN'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {datos.operarios.length > lista.length ? (
        <p className="muted small">Mostrando {lista.length} de {datos.operarios.length}. Usa el buscador para afinar.</p>
      ) : null}
    </div>
  );
}

/**
 * Parámetros del terminal de un cliente.
 *
 * Están aquí además de en el propio terminal porque allí hacen falta permisos
 * de jefe de producción, y quién lo es lo decide uno de estos parámetros: un
 * cliente recién dado de alta no podría configurarse nunca desde su terminal.
 */
function TenantParameters({
  tenant, onClose, onSaved, onError,
}: {
  tenant: Tenant;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [definitions, setDefinitions] = useState<{ key: string; label: string; help: string; type: string; group: string }[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([adminApi.parameters(tenant.slug), adminApi.roles(tenant.slug).catch(() => ({ roles: [] }))])
      .then(([p, r]) => {
        setDefinitions(p.definitions);
        setValues(p.values);
        setRoles(r.roles);
      })
      .catch((e: Error) => onError(e.message))
      .finally(() => setLoading(false));
  }, [tenant.slug, onError]);

  const set = (key: string, value: string) => {
    setValues((v) => ({ ...v, [key]: value }));
    setDirty(true);
  };

  return (
    <Dialog title={`Parámetros de ${tenant.name}`} onClose={onClose} wide>
      {loading ? (
        <Empty icon="⏳" title="Cargando…" />
      ) : (
        <div className="stack">
          {definitions.map((def) => (
            <div key={def.key} className="row" style={{ alignItems: 'flex-start', gap: 18 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 620 }}>{def.label}</div>
                <div className="muted small">{def.help}</div>
              </div>
              {def.key === 'production.supervisorRoleId' && roles.length ? (
                // El rol se elige de los que existen en ESE SAP: teclear un ID
                // a mano es la forma segura de equivocarse.
                <select
                  className="input"
                  style={{ width: 260 }}
                  value={values[def.key] ?? ''}
                  onChange={(e) => set(def.key, e.target.value)}
                >
                  <option value="">— sin definir —</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} (id {r.id})
                    </option>
                  ))}
                </select>
              ) : def.type === 'boolean' ? (
                <button
                  className={`chip${values[def.key] === 'true' ? ' chip--selected' : ''}`}
                  style={{ minWidth: 120 }}
                  onClick={() => set(def.key, values[def.key] === 'true' ? 'false' : 'true')}
                >
                  {values[def.key] === 'true' ? 'Activado' : 'Desactivado'}
                </button>
              ) : (
                <input
                  className="input"
                  style={{ width: 200 }}
                  value={values[def.key] ?? ''}
                  onChange={(e) => set(def.key, e.target.value)}
                />
              )}
            </div>
          ))}
          <div className="row">
            <button className="btn" onClick={onClose}>Cancelar</button>
            <div className="spacer" />
            <button
              className="btn btn--primary btn--lg"
              disabled={!dirty}
              onClick={() =>
                void adminApi
                  .saveParameters(tenant.slug, values)
                  .then(() => onSaved(`${tenant.name}: parámetros guardados y empleados resincronizados`))
                  .catch((e: Error) => onError(e.message))
              }
            >
              Guardar parámetros
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/**
 * Baja de un cliente. Pide escribir su identificador: es irreversible y se
 * lleva los fichajes históricos de ese cliente, que no están en SAP.
 */
function DeleteTenant({
  tenant, onClose, onDone, onError,
}: {
  tenant: Tenant;
  onClose: () => void;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [confirm, setConfirm] = useState('');
  return (
    <Dialog title={`Dar de baja ${tenant.name}`} onClose={onClose}>
      <div className="stack">
        <div className="toast toast--error" style={{ boxShadow: 'none' }}>
          Se borrará su esquema de datos con todos los fichajes, paradas y auditoría de este cliente. Los documentos ya
          contabilizados en SAP no se tocan. No se puede deshacer.
        </div>
        <Field label={`Escribe «${tenant.slug}» para confirmar`}>
          <input className="input mono" value={confirm} autoFocus onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <div className="row">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <div className="spacer" />
          <button
            className="btn btn--danger btn--lg"
            disabled={confirm !== tenant.slug}
            onClick={() =>
              void adminApi
                .deleteTenant(tenant.slug, confirm)
                .then((r) =>
                  onDone(
                    `${tenant.name} dado de baja: ${r.sociedades} sociedad(es) y ${r.fichajes} fichajes borrados con el esquema ${r.schema}.`,
                  ),
                )
                .catch((e: Error) => onError(e.message))
            }
          >
            Dar de baja definitivamente
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function AdminLogin({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [now] = useState(new Date());

  const submit = async () => {
    try {
      const { token } = await adminApi.login(password);
      setToken(token);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setPassword('');
    }
  };

  return (
    <div className="app">
      <div className="content" style={{ display: 'grid', placeItems: 'center' }}>
        <div className="card stack" style={{ width: 'min(460px, 100%)' }}>
          <div className="eyebrow">ECINSA · Terminal de producción</div>
          <h1 className="h1">Administración</h1>
          <p className="muted small" style={{ margin: 0 }}>
            Alta y mantenimiento de clientes. {clockTime(now)}
          </p>
          <Field label="Contraseña del panel">
            <input
              className="input"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void submit()}
            />
          </Field>
          {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}
          <button className="btn btn--primary btn--lg btn--block" onClick={() => void submit()}>
            Entrar
          </button>
        </div>
      </div>
    </div>
  );
}

function TenantForm({
  tenant, hubTunnels, tenantDomain, onClose, onSaved, onError,
}: {
  tenant: Tenant | null;
  hubTunnels: HubTunnel[];
  tenantDomain: string;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [slug, setSlug] = useState(tenant?.slug ?? '');
  const [name, setName] = useState(tenant?.name ?? '');
  const [hubClient, setHubClient] = useState(tenant?.hubClient ?? hubTunnels[0]?.name ?? '');
  const [slBaseUrl, setSlBaseUrl] = useState(tenant?.slBaseUrl ?? PLANTILLA_SL);

  /**
   * Al elegir un túnel se rellena sola la dirección del Service Layer con su IP.
   *
   * Solo se pisa lo que nadie ha escrito a mano: la plantilla vacía o la
   * dirección derivada de otro túnel. Teclear esa IP a mano es exactamente
   * cómo un cliente se queda apuntando a una dirección que ya no es la suya y
   * nadie se entera hasta días después.
   */
  const elegirTunel = (t: HubTunnel) => {
    setHubClient(t.name);
    if (t.ip === null) return;
    const derivadas = hubTunnels.filter((x) => x.ip !== null).map((x) => urlServiceLayer(x.ip as string));
    const actual = slBaseUrl.trim();
    if (actual === '' || actual === PLANTILLA_SL || derivadas.includes(actual)) {
      setSlBaseUrl(urlServiceLayer(t.ip));
    }
  };
  const [verify, setVerify] = useState(tenant?.slVerifySsl ?? false);
  const [timeout, setTimeoutMs] = useState(String(tenant?.slTimeoutMs ?? 30000));
  const [active, setActive] = useState(tenant?.active ?? true);

  const save = async () => {
    const input = {
      slug, name, hubClient, slBaseUrl,
      slVerifySsl: verify,
      slTimeoutMs: Number(timeout) || 30000,
      active,
    };
    try {
      if (tenant) {
        await adminApi.updateTenant(tenant.slug, input);
        onSaved(`${name}: cambios guardados. El cliente se reconstruye en la próxima petición.`);
      } else {
        await adminApi.createTenant(input);
        onSaved(`${name} dado de alta en https://${slug}.${tenantDomain}. Añádele ahora sus sociedades.`);
      }
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <Dialog title={tenant ? `Editar ${tenant.name}` : 'Nuevo cliente'} onClose={onClose} wide>
      <div className="stack">
        <div className="grid-2">
          <Field label="Identificador (subdominio del terminal)">
            <input
              className="input"
              value={slug}
              disabled={Boolean(tenant)}
              placeholder="demo"
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
            />
          </Field>
          <Field label="Nombre del cliente">
            <input className="input" value={name} placeholder="ECINSA Demo" onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>
        <p className="muted small" style={{ margin: 0 }}>
          Su terminal quedará en <span className="mono">https://{slug || '<identificador>'}.{tenantDomain}</span>.
          El identificador no se puede cambiar después: es el nombre del esquema donde viven sus datos.
        </p>

        <Field label="Túnel en el hub (X-Ecinsa-Cliente)">
          <div className="chips">
            {hubTunnels.map((t) => (
              <button
                key={t.name}
                className={`chip${hubClient === t.name ? ' chip--selected' : ''}`}
                onClick={() => elegirTunel(t)}
                title={t.ip === null ? 'El hub no ha dicho su IP' : `Service Layer en ${t.ip}:50000`}
              >
                {t.name}
              </button>
            ))}
          </div>
        </Field>
        <p className="muted small" style={{ margin: 0 }}>
          Es el nombre exacto dado de alta en el hub, con sus mayúsculas. Dice a dónde va la petición; no identifica de
          quién es el dato: eso es el CIF de cada sociedad. La lista la da el hub, así que un cliente dado de alta allí
          aparece aquí sin tocar nada.
        </p>

        <Field label="Service Layer (por la IP del túnel del hub)">
          <input
            className="input mono"
            value={slBaseUrl}
            placeholder="https://10.30.0.4:50000/b1s/v1"
            onChange={(e) => setSlBaseUrl(e.target.value)}
          />
        </Field>
        <p className="muted small" style={{ margin: 0 }}>
          Nunca la IP de la red local del cliente: el túnel termina en un Windows que reenvía por portproxy, así que SAP
          responde en la IP del túnel (10.30.0.X) y no en la de su LAN.
        </p>

        <div className="grid-2">
          <Field label="Tiempo máximo de espera (ms)">
            <input className="input mono" value={timeout} onChange={(e) => setTimeoutMs(e.target.value)} />
          </Field>
          <div className="stack">
            <span className="field__label">Opciones</span>
            <div className="chips">
              <button className={`chip${verify ? ' chip--selected' : ''}`} onClick={() => setVerify((v) => !v)}>
                {verify ? 'Certificado verificado' : 'Certificado sin verificar'}
              </button>
              <button className={`chip${active ? ' chip--selected' : ''}`} onClick={() => setActive((v) => !v)}>
                {active ? 'Activo' : 'Desactivado'}
              </button>
            </div>
          </div>
        </div>

        <div className="row">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <div className="spacer" />
          <button className="btn btn--primary btn--lg" onClick={() => void save()}>
            {tenant ? 'Guardar cambios' : 'Dar de alta'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function CompanyForm({
  tenant, company, onClose, onSaved, onError,
}: {
  tenant: Tenant;
  company: Company | null;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [companyDb, setCompanyDb] = useState(company?.companyDb ?? '');
  const [name, setName] = useState(company?.name ?? '');
  const [cif, setCif] = useState(company?.cif ?? '');
  const [user, setUser] = useState(company?.slUsername ?? 'manager');
  const [password, setPassword] = useState('');
  const [isDefault, setIsDefault] = useState(company?.isDefault ?? tenant.companies.length === 0);
  const [active, setActive] = useState(company?.active ?? true);
  const [cifState, setCifState] = useState<{ valid: boolean; foreign: boolean; reason: string | null; value: string } | null>(null);
  const [probe, setProbe] = useState<{ ok: boolean; message: string; ms: number } | null>(null);
  const [probing, setProbing] = useState(false);

  // El CIF se comprueba mientras se teclea: el hub valida el dígito de control
  // y un CIF mal tecleado sería rechazado allí, lejos de quien lo escribió.
  useEffect(() => {
    if (cif.trim() === '') {
      setCifState(null);
      return;
    }
    const timer = setTimeout(() => {
      void adminApi.checkCif(cif).then(setCifState).catch(() => setCifState(null));
    }, 350);
    return () => clearTimeout(timer);
  }, [cif]);

  /**
   * Prueba las credenciales tal y como están escritas, sin guardar nada. Es
   * donde uno se da cuenta de que el usuario o la base de datos no son los que
   * creía, antes de dejar una sociedad a medias en el registro.
   */
  const probar = async () => {
    setProbing(true);
    setProbe(null);
    try {
      const result = await adminApi.probe(tenant.slug, {
        companyDb,
        slUsername: user,
        slPassword: password,
        ...(company ? { companyId: company.id } : {}),
        cif,
      });
      setProbe({ ok: result.ok, message: result.message, ms: result.ms });
    } catch (err) {
      setProbe({ ok: false, message: err instanceof ApiError ? err.message : String(err), ms: 0 });
    } finally {
      setProbing(false);
    }
  };

  const save = async () => {
    try {
      await adminApi.saveCompany(tenant.slug, company?.id ?? null, {
        companyDb, name, cif, slUsername: user, slPassword: password, isDefault, active,
      });
      onSaved(
        cif.trim()
          ? `${name}: guardada con CIF ${cifState?.value ?? cif}`
          : `${name}: guardada SIN CIF. Sus peticiones saldrán sin X-Ecinsa-CIF hasta que lo rellenes.`,
      );
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <Dialog
      title={company ? `Editar ${company.name}` : `Nueva sociedad de ${tenant.name}`}
      subtitle="Una sociedad es una company_db de SAP con su propio CIF. Un cliente puede tener varias."
      onClose={onClose}
      wide
    >
      <div className="stack">
        <div className="grid-2">
          <Field label="company_db en SAP">
            <input className="input mono" value={companyDb} placeholder="SBODEMOES" onChange={(e) => setCompanyDb(e.target.value)} />
          </Field>
          <Field label="Nombre de la sociedad">
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>

        <Field label="CIF de esta sociedad">
          <input
            className="input mono"
            value={cif}
            placeholder="B12345674 — déjalo vacío si todavía no lo tienes"
            onChange={(e) => setCif(e.target.value)}
          />
        </Field>
        {cifState ? (
          <div
            className="small"
            style={{ color: cifState.valid ? (cifState.foreign ? 'var(--warn)' : 'var(--ok)') : 'var(--danger)' }}
          >
            {cifState.valid
              ? cifState.foreign
                ? `Se guardará como ${cifState.value}. Identificador extranjero: no se puede comprobar el dígito de control.`
                : `Correcto. Se guardará como ${cifState.value}.`
              : `No válido: ${cifState.reason}`}
          </div>
        ) : (
          <p className="muted small" style={{ margin: 0 }}>
            Sin CIF el terminal funciona igual, pero sus peticiones salen sin matrícula y no se podrán enrutar por CIF.
            Es preferible dejarlo vacío a poner uno supuesto: un CIF equivocado atribuye los datos a otra empresa.
          </p>
        )}

        <div className="grid-2">
          <Field label="Usuario de SAP">
            <input className="input mono" value={user} onChange={(e) => setUser(e.target.value)} />
          </Field>
          <Field label={company ? 'Contraseña (vacío = no cambiarla)' : 'Contraseña de SAP'}>
            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
        </div>

        <div className="row">
          <button className="btn" disabled={probing || !companyDb || !user} onClick={() => void probar()}>
            {probing ? 'Probando…' : '⚡ Probar Service Layer'}
          </button>
          <span className="muted small">
            Comprueba estas credenciales contra {tenant.slBaseUrl} sin guardar nada.
          </span>
        </div>
        {probe ? (
          <div
            className={`toast ${probe.ok ? 'toast--ok' : 'toast--error'}`}
            style={{ boxShadow: 'none' }}
          >
            {probe.ok ? '✓ ' : '✗ '}
            {probe.message}
            {probe.ms ? ` (${probe.ms} ms)` : ''}
          </div>
        ) : null}

        <div className="chips">
          <button className={`chip${isDefault ? ' chip--selected' : ''}`} onClick={() => setIsDefault((v) => !v)}>
            {isDefault ? 'Sociedad predeterminada' : 'No predeterminada'}
          </button>
          <button className={`chip${active ? ' chip--selected' : ''}`} onClick={() => setActive((v) => !v)}>
            {active ? 'Activa' : 'Inactiva'}
          </button>
        </div>

        <div className="row">
          {company ? (
            <button
              className="btn btn--danger"
              onClick={() =>
                void adminApi
                  .deleteCompany(tenant.slug, company.id)
                  .then(() => onSaved(`${company.name}: eliminada`))
                  .catch((e: Error) => onError(e.message))
              }
            >
              Eliminar
            </button>
          ) : null}
          <div className="spacer" />
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button
            className="btn btn--primary btn--lg"
            disabled={!companyDb || !name || (cifState !== null && !cifState.valid)}
            onClick={() => void save()}
          >
            Guardar sociedad
          </button>
        </div>
      </div>
    </Dialog>
  );
}
